import type React from "react";
import { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { Roles } from "api/schemas/shared/role";
import { useGetPatientTreatmentScheduleWeek } from "api/services/treatment/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import {
  hasOverdueActivities,
  hasOverdueRequiredActivities,
  hasRequiredActivities,
} from "routes/patients/utils/activityHelpers";
import { useGroupScheduleWeek } from "routes/patients/utils/hooks/useGroupScheduleWeek";
import { Divider } from "shared/atoms/Divider";
import { Notification } from "shared/atoms/Notification";
import { Spinner } from "shared/atoms/Spinner";
import { CurrentPatientContext } from "utils/contexts";
import { userIsPartner } from "utils/profile/profileHelper";

import { Calendar } from "./Calendar";
import { ProtocolActivityGroup } from "./ProtocolActivityGroup";
import { ProtocolHeader } from "./ProtocolHeader";
import { ProtocolTimeline } from "./ProtocolTimeline";

export const ProtocolView: React.FC = () => {
  const { patient, previousTreatmentId } = useContext(CurrentPatientContext);
  const { patientId, weekInfo } = useParams<{ patientId?: string; weekInfo?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [requiredVisible, setRequiredVisible] = useState(false);
  const { profile } = useProfileContext();
  const { groupData, groupOverdueActivities } = useGroupScheduleWeek();

  invariant(profile);

  const getTreatmentId = () => {
    if (previousTreatmentId) {
      return previousTreatmentId;
    }
    if (patient.treatments && patient.treatments.length > 0) {
      // TODO: Fix when typing patient context
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return patient.treatments.find((treatment: any) => treatment.is_current_treatment === true).id || null;
    }
    return null;
  };

  const { data, isError, isLoading } = useGetPatientTreatmentScheduleWeek({
    locale: profile.locale as string,
    patient_id: parseInt(patientId || "", 10),
    week: weekInfo === "current" ? patient.week : parseInt(weekInfo || "", 10),
    treatment_id: getTreatmentId(),
    role: userIsPartner(profile) ? Roles.ClinicPartner : Roles.Therapist,
  });

  const handlePageChange = (direction: string) => {
    if (!data) return;

    if (direction === "next") {
      navigate(`/patients/${patientId}/protocol/${data.week.week_number + 1}`);
    } else if (direction === "previous") {
      navigate(`/patients/${patientId}/protocol/${data.week.week_number - 1}`);
    }
  };

  const handleCurrentClick = () => {
    navigate(`/patients/${patientId}/protocol/current`);
  };

  const selectChange = (dayIndex: number) => {
    if (!data) return;

    if (hasRequiredActivities(data.days, dayIndex)) {
      setRequiredVisible(true);
    } else {
      setRequiredVisible(false);
    }
  };

  useEffect(() => {
    if (!data) return;

    if (hasRequiredActivities(data.days)) {
      setRequiredVisible(true);
    } else {
      setRequiredVisible(false);
    }
  }, [data]);

  return (
    <>
      {!isError && data && (
        <>
          <ProtocolHeader
            calculated_duration_weeks={data?.calculated_duration_weeks}
            empty={!!isError}
            hasOverdueRequiredActivities={hasOverdueRequiredActivities(data.days)}
            week={data.week}
            onPageChange={direction => handlePageChange(direction)}
            onCurrentClick={() => handleCurrentClick()}
          />

          {patient.schedule_version === 2 ? (
            <ProtocolTimeline
              patient={{
                id: patient.id,
                // TODO: Fix when typing patient context
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                treatmentID: patient?.treatments.find((treatment: any) => treatment.is_current_treatment === true).id,
                week: patient.week,
              }}
              weekNumber={weekInfo === "current" ? patient.week : parseInt(weekInfo || "", 10)}
            />
          ) : (
            <>
              {hasOverdueActivities(data.days) && <ProtocolActivityGroup activities={groupOverdueActivities(data)} />}
              <CalendarScrollWrapper>
                <Calendar days={groupData(data)} patient_id={patient.id} onSelectChange={day => selectChange(day)} />
              </CalendarScrollWrapper>
            </>
          )}

          <BottomWrapper>
            <Divider />
            {requiredVisible && <RequiredText>{t("patients.protocol.required")}</RequiredText>}
          </BottomWrapper>
        </>
      )}

      {(isError || isLoading) && (
        <CenterContainer>
          {isError && <Notification variant="danger">{t("errors.generic")}</Notification>}
          {isLoading && <Spinner small />}
        </CenterContainer>
      )}
    </>
  );
};

const CenterContainer = styled.div`
  height: 300px;
  width: 100%;
  background-color: #fafbfc;
  display: flex;
  padding-top: ${props => props.theme.spacing.S_30};
  align-items: flex-start;
  justify-content: center;
`;

const CalendarScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 5px;
    margin: 0 16px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
`;

const BottomWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 32px auto;
  padding: 0 ${props => props.theme.spacing.S_30};
`;

const RequiredText = styled.div`
  width: 100%;
  ${props => props.theme.font.header5}
  color: #1a2332;
  font-weight: 600;
  padding: ${props => props.theme.spacing.S_10} 0 ${props => props.theme.spacing.S_60};
  letter-spacing: -0.01em;
`;
